import { useForm, useFieldArray } from "react-hook-form";
import './CreateQuiz.scss';
import images from "../../assets/images";
import { FC, useEffect, useState } from "react";
import * as yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import { FETCH_POLICY_NO_CACHE, messages, patterns, theme } from "../../utils/constants";
import { Dialog, ThemeProvider } from '@mui/material';
import { useAppSelector } from "../../redux/app/hooks";
import { useMutation } from "@apollo/client";
import { CREATE_QUIZ, EDIT_QUIZ } from "../../pages/Quiz/api/QuizQuery";
import ToasterWithMessage from "../toaster";
import { questions, QuizData } from "../../pages/Quiz/Quiz";
import { getScreenDimensions } from "../../utils/helper";
import ConfirmationPopup from "../ConfirmationPopup/ConfirmationPopup";
import { trackButtonClick } from "../../firebase/analyticsHelper";
import { ButtonClickEvents } from "../../firebase/constants/events";

type CreateQuizType = {
    setShowCreate: (val: boolean) => void;
    setIsEdit: (val: boolean) => void;
    setSelectedQuiz: (val: QuizData | undefined) => void;
    isEdit: boolean;
    selectedQuiz: QuizData | undefined;
}

type Option = {
    value: string;
    isCorrect: boolean;
};

type Quiz = {
    question: string;
    options: Option[];
};

type FormValues = {
    quiz: Quiz[];
};

const schema = yup.object().shape({
    quiz: yup.array().of(
      yup.object().shape({
        question: yup.string().required(messages.question).matches(patterns.startingWithSpaces, messages.validQuestion).trim(),
        options: yup.array().of(
          yup.object().shape({
            value: yup.string().required(messages.option).trim().matches(patterns.startingWithSpaces, messages.validOption),
            isCorrect: yup.boolean().required()
          })
        ).required(),
      })
    ).required()
  });

const inititalValue: Quiz = { 
    question: "", 
    options: [{ value: "", isCorrect: true }, { value: "", isCorrect: false }] 
};

const CreateQuiz: FC<CreateQuizType> = ({ selectedQuiz, isEdit, setShowCreate, setIsEdit, setSelectedQuiz }) => {
    const { closeIcon, DeleteQuiz, quizIcon } = images;
    const [openPopup, setOpenPopup] = useState<boolean>(false);
    const [isDone, setIsDone] = useState<boolean>(false);
    const { register, control, handleSubmit, reset, watch, formState : {errors} } = useForm<FormValues>({ defaultValues: {quiz: [inititalValue]}, resolver: yupResolver(schema)});
    const { fields, append, remove, update } = useFieldArray({ control, name: "quiz" });
    const [ quizName, setQuizName ] = useState<string | null>();
    const selectedSubjectId = localStorage.getItem('selectedSId');
    const selectedClassRoomId = useAppSelector((state) => state.auth.selectedClassRoomId);
    const [validationError, setValidationError] = useState<string | null>();
    const { success, error } = ToasterWithMessage();
    const [openReminder, setOpenReminder] = useState<boolean>(false);

    const [createQuiz,{loading}] = useMutation(CREATE_QUIZ, {
        fetchPolicy: FETCH_POLICY_NO_CACHE,
        onCompleted: (data) => {
            setOpenPopup(false);
            setShowCreate(false);
            success(data?.createNewQuiz?.message);
            reset();
        },
        onError: (err) => {
            error(err?.message);
        }
    })

    const [editQuiz,{loading: EditLoading}] = useMutation(EDIT_QUIZ, {
        fetchPolicy: FETCH_POLICY_NO_CACHE,
        onCompleted: (data) => {
            setOpenPopup(false);
            setShowCreate(false);
            setIsEdit(false);
            setSelectedQuiz(undefined);
            success(data?.editQuiz);
            reset();
        },
        onError: (err) => {
            error(err?.message);
        }
    })

    const handleRemove = (index: number) => {
        if (fields.length > 1) {
            remove(index);
        }
    }
    
    const handleAdd = () => {
        append(inititalValue);
    }

    const handleAddOption = (index: number) => {
        const currentOptions = watch(`quiz.${index}.options`);
        const currentQuestion = watch(`quiz.${index}.question`);
        if (currentOptions.length < 6) {
            const updatedOptions = [...currentOptions, { value: "", isCorrect: false }];
            update(index, { ...fields[index], question: currentQuestion, options: updatedOptions });
        }
    }

    const handleRemoveOption = (index: number, optionIndex: number) => {
        const currentOptions = watch(`quiz.${index}.options`);
        const currentQuestion = watch(`quiz.${index}.question`);
        const updatedOptions = [...currentOptions];
        updatedOptions.splice(optionIndex,1);
        update(index, { ...fields[index], question: currentQuestion, options: updatedOptions });
    }

    const onSubmit = () => {
        trackButtonClick(ButtonClickEvents?.QUIZ_SUBMIT, {
            description : "User clicked submit button in create/edit quiz",
            page: isEdit ? 'Edit Quiz' : 'Create Quiz'
        });
        // setOpenPopup(true);
        setOpenReminder(true);
    }

    const onYesReminder = () => {
        setOpenReminder(false);
        setOpenPopup(true);
    }
    const validateQuizName = (value: string | null | undefined) => {
        if (!value || value === "") {
          return messages.quizName;
        }
        if (!patterns.startingWithSpaces.test(value)) {
            return messages.validQuizName;
        }
        if (!patterns.quizNameLengthRegex.test(value)) {
          return messages.quizNameLength;
        }
        if (!patterns.fileNameRegex.test(value)) {
          return messages.validQuizName;
        }
        // if (patterns.consecutiveSpecialCharsRegex.test(value)) {
        //   return messages.quizConsecutiveSpecialChars;
        // }
        // if (!patterns.filenameAtleast1Alpha.test(value)) {
        //   return messages.quiznameAtlease1Alpha;
        // }
        return null;
    };

    const handleQuizName = (val: string) => {
        setQuizName(val)
        if (isDone) {
            setValidationError(validateQuizName(val));
        }
    }

    const closePopup = (data: FormValues) => {        
        if (!isDone) {
            setIsDone(true);
        }
        const validateError = validateQuizName(quizName)
        setValidationError(validateError);
        if (!validateError) {
            const questions = data?.quiz?.map((val: Quiz) => {
                const temp: any = { qs_info: val?.question?.trim() };
                const opt: any = {};
                val?.options?.forEach((option: Option, index: number) => {
                    opt[String.fromCharCode(65 + index)] = option?.value?.trim();
                    if (option?.isCorrect) {
                        temp["is_correct"] = String.fromCharCode(65 + index);
                    }
                });
                return { ...temp, options: opt }
            })
            if (isEdit) {
                editQuiz({
                    variables: {
                        input: {
                            qzId: selectedQuiz?.quizId,
                            cId: selectedClassRoomId,
                            sjId: selectedSubjectId,
                            qz_title: quizName?.trim(),
                            questions
                        }
                    }
                })
            }
            else {
                createQuiz({
                    variables: {
                        input: {
                            cId: selectedClassRoomId,
                            sjId: selectedSubjectId,
                            qz_title: quizName?.trim(),
                            questions
                        }
                    }
                });
            }
        }
    }

    const handleCorrectOptions = (index: number, correctIndex: number) => {
        const currentOptions = watch(`quiz.${index}.options`);
        const currentQuestion = watch(`quiz.${index}.question`);
        const updatedOptions = currentOptions.map((option, idx) => ({
            ...option,
            isCorrect: idx === correctIndex
        }));
        update(index, { ...fields[index], question: currentQuestion, options: updatedOptions });
    }

    useEffect(() => {
        if (isEdit && selectedQuiz) {
            const quizes = selectedQuiz?.questions?.map((val: questions, index: number) => {
                return {
                    question: val?.qs_info,
                    options: Object.values(val?.options)?.map((option: string, optionIndex: number) => {
                        return {
                            value: option,
                            isCorrect: Object.keys(val?.options)?.[optionIndex] === val?.is_correct
                        }
                    })
                }              
            })
            reset({
                quiz: quizes
            })
            handleQuizName(selectedQuiz?.quizTitle);
        }
    }, [isEdit, selectedQuiz])

    return (
        <>
        <div className="CreateQuizMainContainer"> 
            <form id="createQuizForm" className="QuizformContainer" onSubmit={handleSubmit(onSubmit)}>
                {fields.map((item, index) => {
                    return (
                        <div className="mainFormContainer" key={index}>
                            <div className="titleContainer">
                                <div className="title">{`Question ${ index + 1}` }</div>
                                <div className="icCancel" onClick={() => handleRemove(index)}>
                                    <img className="unionIcon" alt="closeIcon" src={closeIcon} />
                                </div>
                            </div>
                            <div className="innerFormContainer" key={item.id}>
                                <div className="optionContainer"><textarea className="quiz_input_question no-resize" placeholder="Type your question here" {...register(`quiz.${index}.question`)}/></div>
                                {errors?.quiz?.[index]?.question && <span className="error">{errors?.quiz?.[index]?.question?.message}</span>}
                                {
                                    item?.options.map((optionItem: Option, optionIndex: number) => {
                                        return (
                                            <>
                                                <div className="optionContainer" key={optionIndex}>
                                                    <div className="optionIndex">{String.fromCharCode(65 + optionIndex)}</div>
                                                    <input className="quiz_input_option" {...register(`quiz.${index}.options.${optionIndex}.value`)} />
                                                    <div className={optionItem?.isCorrect ? "correctOption" : "wrongOption"} onClick={() => handleCorrectOptions(index, optionIndex)} />
                                                    {(item.options.length > 2 && !optionItem?.isCorrect) && <div className={"removeIcon"} onClick={() => handleRemoveOption(index, optionIndex)}><span>{"-"}</span></div>}
                                                </div>
                                                {errors?.quiz?.[index]?.options?.[optionIndex] && <span className="error optionError">{errors?.quiz?.[index]?.options?.[optionIndex]?.value?.message}</span>}
                                            </>
                                        )
                                    })
                                }
                            </div>
                            {item?.options.length < 6 && <div className="addAnswersContainer">
                           { getScreenDimensions().isMobileScreen && <div className="deltequiz" onClick={() => handleRemove(index)}>
                                    <img className="deleteIcon" alt="deleteIcon" src={DeleteQuiz} />
                                </div>}
                                <span className="addAnswerBtn" onClick={() => handleAddOption(index)}>Add answers +</span>
                            </div>}
                        </div>
                    );
                })}
                <div className="addQuestion" onClick={handleAdd}>Add Question +</div>
            </form>
            <ThemeProvider theme={theme}>
                <Dialog open={openPopup} classes={{ paper: 'saveQuizPaperStyle' }} onClose={() => setOpenPopup(false)}>
                <form onSubmit={handleSubmit(closePopup)} className='popupContainer'>
                    <div className='messageContainer'>Save Quiz</div>
                    <div className="quizNameContainer">
                        <input type="text" value={quizName || ""} className="quizName"placeholder="Type quiz title name here..." onChange={(e) => handleQuizName(e.target.value)} />
                        {validationError && <span className="error">{validationError}</span>}
                    </div>
                    <div className="btnContainer">
                        <button type="submit" className='okButtonStyle' disabled={loading || EditLoading}>Done</button>
                    </div>
                </form>
                </Dialog>
            </ThemeProvider>
        </div>
        <ConfirmationPopup
            open={openReminder}
            page={`quiz`}
            gifData={quizIcon}
            message={`Please ensure that a correct answer is selected for each question before submitting the quiz.`}
            yesButtonFunctionality={onYesReminder}
            messageWidth={'300px'}
        />
        </>
    )
}

export default CreateQuiz;